import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { NotificationsService } from '../../services/notifications.service';
import { PGTask, TasksService } from '../../services/tasks.service';
import { RemoteTaskJob, RemoteTasksService } from '../../services/remote-tasks.service';
import { PgNotificationListComponent } from '../pg-notification-list/pg-notification-list.component';

@Component({
    selector: 'app-pg-tasks-view',
    templateUrl: './pg-tasks-view.component.html',
    styleUrls: ['./pg-tasks-view.component.scss'],
    standalone: false
})
export class PgTasksViewComponent implements OnInit, OnDestroy {

    constructor(
        private tasksService: TasksService,
        private remoteTasksService: RemoteTasksService,
        private notificationsService: NotificationsService
    ) { }

    tasks: Array<PGTask> = null;
    remoteJobs: Array<RemoteTaskJob> = null;

    @ViewChild(NgbDropdown) dropdownComponent: NgbDropdown;
    @Output() openChange = new EventEmitter<boolean>()

    private _taskServiceSubscription: Subscription = null;
    private _remoteTasksSubscription: Subscription = null;

    ngOnInit(): void {
        this.tasks = this.tasksService.tasks;
        this.remoteJobs = this.remoteTasksService.jobs;

        this._taskServiceSubscription = this.tasksService.statusChange.subscribe((change) => {
            if(change.action == 'add') {
                this.dropdownComponent.open();
            }
        })

        this._remoteTasksSubscription = this.remoteTasksService.jobsChange.subscribe((change) => {
            this.remoteJobs = this.remoteTasksService.jobs;
            if(change.action == 'upsert') {
                this.dropdownComponent?.open();
            }
        })
    }

    ngOnDestroy() {
        if(this._taskServiceSubscription != null) this._taskServiceSubscription.unsubscribe();
        if(this._remoteTasksSubscription != null) this._remoteTasksSubscription.unsubscribe();
    }

    getProgressBarClass(status: string) {
        switch(status) {
            case 'waiting': return 'bg-secondary opacity-00';
            case 'success': return 'bg-success';
            case 'error': return 'bg-danger';
            case 'running': return 'progress-bar-striped progress-bar-animated';
        }
    }

    getSubProgressBarClass(status: string) {
        switch(status) {
            case 'success': return 'bg-success';
            case 'error': return 'bg-danger';
            default: return 'bg-secondary';
        }
    }

    getProgressBarTransform(status: string, progress: number) {
        if(status == 'running') return 'translate(' + ((progress || 0) * 100 - 100) + '%, 0)';
        else return null
    }

    formatProgressPercent(progress: number, status?: string) {
        if(status == 'success') return '100%';
        if(progress == null) return '';
        return Math.round(progress * 100) + '%';
    }

    isTaskFinished(task: PGTask) {
        return task.status == 'success' || task.status == 'error';
    }

    isRemoteJobFinished(job: RemoteTaskJob) {
        let _ui = this.remoteTasksService.getUiStatus(job);
        return _ui == 'success' || _ui == 'error';
    }

    removeLocalTask(task: PGTask) {
        this.tasksService.removeTask(task);
    }

    removeRemoteJob(job: RemoteTaskJob) {
        this.remoteTasksService.dismiss(job.id).catch(() => {
            // keep job visible if dismiss fails
        });
    }

    redoErrorTask(task: PGTask) {
        this.tasksService.removeTask(task);
        this.tasksService.addTask(new PGTask(task.action, task.name, task.description))
    }

    getUnreadNotificationsCount() {
        return this.notificationsService.unreadCount;
    }

    getRemoteUiStatus(job: RemoteTaskJob | { status?: RemoteTaskJob['status'] }) {
        return this.remoteTasksService.getUiStatus(job);
    }

    getRemoteJobProgress(job: RemoteTaskJob) {
        return this.remoteTasksService.getJobProgress(job);
    }

    getLocalStatusMessage(task: PGTask): string {
        if(task.errorMessage) return task.errorMessage;
        if(task.status == 'success') return 'Completato';
        if(task.status == 'error') return 'Errore';
        return null;
    }

    getRemoteStatusMessage(job: RemoteTaskJob): string {
        if(job.message) return job.message;
        let _ui = this.getRemoteUiStatus(job);
        if(_ui == 'success') return 'Completato';
        if(_ui == 'error') return 'Errore';
        return null;
    }

    @ViewChild(PgNotificationListComponent) notificationListComponent: PgNotificationListComponent

    onOpenChange(isOpen: boolean) {
        if(isOpen) {
            this.notificationListComponent.reset()
        }
        else {
            this.notificationListComponent.markAsRead()
        }
    }
}
